<template>
  <FlotoDrawer width="70%" :open="open" @hide="$emit('close')" @show="getTask">
    <template v-slot:trigger="slotData">
      <slot name="trigger" v-bind="slotData"><span /></slot>
    </template>
    <template v-if="task" v-slot:title>
      <h5 class="mb-0"> {{ task.name }}: {{ task.subject }} </h5>
      <div v-if="task.owner && task.createdAt" class="flex justify-between">
        <small class="text-neutral">
          {{ $t('by') }}
          <FlotoUserDrawer :disabled="isPortalLogin" :user-id="task.owner" />
          {{ $t('at') }}
          {{ task.createdAt | datetime }}
          ({{ task.createdAt | timeago }})
        </small>
      </div>
    </template>

    <MRow v-if="task">
      <MCol class="min-w-0 flex flex-1">
        <MTab
          default-active="details"
          size="small"
          class="flex flex-1 flex-col"
          @change="handleTabChange"
        >
          <MTabPane key="details" :tab="$t('details')">
            <Task
              :allow-edit="false"
              :allow-task-header="false"
              :allow-delete="false"
              :task="task"
              :disabled="disabled || detailsDisabled"
              :is-in-active-task="hideConversation"
              v-on="taskListeners"
            >
              <template v-slot:task-actions>
                <span />
              </template>
            </Task>
            <AdditionalInfoWithExpand
              :resource="task"
              :module-name="$constants.TASK"
              :show-no-data="false"
              disabled
              :is-approval="false"
              :is-form-rules-available="false"
              :update-fn="(e) => e"
            />
            <div v-if="relatedResourceType" class="my-2 bordered rounded">
              <ResourceListTitleWithStatus
                :module-name="relatedResourceType"
                bordered
                :use-link="!isPortalLogin"
                :link="resourceLink"
                :resource="relatedResource"
              />
            </div>
            <div
              v-if="task.description && hasFieldViewPermission('description')"
            >
              <small class="text-neutral-light">{{ $t('description') }}</small>
              <div class="mt-2">
                <FlotoRichEditor :value="task.description" disabled />
              </div>
            </div>
            <div v-if="(task.fileAttachments || {}).length">
              <FlotoAttachment :value="task.fileAttachments" as-link disabled />
            </div>
          </MTabPane>
          <MTabPane
            v-if="hideConversation === false"
            key="comments"
            :tab="$tc('comment', 2)"
          >
            <ConversationContainer
              :resource="task"
              :disabled="disabled || !canUpdateTask(task.moduleName)"
              :allowed-types="['comment_on_task']"
              module-name="task"
              :use-private-indicator="false"
              :resource-id="task.id"
            />
          </MTabPane>
          <MTabPane
            v-if="hideWork === false"
            key="worklog"
            :tab="$t('work_log')"
          >
            <WorkLogContainer
              :disabled="disabled || !canUpdateTask(task.moduleName)"
              module-name="task"
              :resource-id="task.id"
              :custom-rules="
                task.moduleName === $constants.PROJECT
                  ? { allowedWorklogAccess }
                  : { isAllowedWorklogAccessForTask: true }
              "
              :resource="task"
              @refresh-worklog="getWorklogTotalTime"
            >
              <template
                v-if="task.moduleName === $constants.PROJECT"
                v-slot:before-add-controls
              >
                <MTooltip
                  v-if="estimatedTime && estimatedTime !== '---'"
                  class="mr-2"
                >
                  <template v-slot:trigger>
                    <MIcon
                      name="hourglass"
                      class="mr-1 text-neutral-light md-text-base"
                    />
                    <span> {{ estimatedTime }} </span>
                  </template>
                  {{ $t('estimates') }}
                </MTooltip>
                <MTooltip
                  v-if="worklogTotalTime && worklogTotalTime !== '---'"
                  class="mr-2"
                >
                  <template v-slot:trigger>
                    <MIcon
                      name="hourglass"
                      class="mr-1 text-neutral-light md-text-base"
                    />
                    <span>
                      {{ worklogTotalTime }}
                    </span>
                  </template>
                  {{ $t('worklog') }}
                  {{ $t('time', { type: $t('total') }) }}
                </MTooltip>
                <MTooltip
                  v-if="remainingTime && remainingTime !== '---'"
                  class="mr-2"
                >
                  <template v-slot:trigger>
                    <MIcon
                      name="clock"
                      class="mr-1 text-neutral-light md-text-base"
                    />
                    <span>
                      {{ remainingTime }}
                    </span>
                  </template>
                  {{ $t('remaining') }} {{ $tc('time') }}
                </MTooltip>
              </template>
            </WorkLogContainer>
          </MTabPane>
          <MTabPane
            v-if="task.moduleName === $constants.PROJECT"
            key="checklist"
            :tab="$t('check_list')"
          >
            <CheckListContainer
              :resource="task"
              :disabled="disabled"
              module-name="task"
              :resource-id="task.id"
            />
          </MTabPane>
          <MTabPane
            v-if="allowAuditContainer"
            key="audit"
            :tab="$t('audit_trail')"
          >
            <AuditContainer
              ref="auditContainer"
              module-name="task"
              :resource-id="task.id"
            />
          </MTabPane>
        </MTab>
      </MCol>
    </MRow>
    <template v-slot:actions="{ hide }">
      <slot name="actions" :hide="hide">
        <MButton
          v-if="
            task.statusId === taskFindStatus('closed').id &&
            canReopenTask(task.moduleName)
          "
          id="reopen-btn"
          class="mr-2"
          :aria-label="`${$t('reopen')} ${$tc('task')}`"
          :disabled="
            isDisabledUserToReopenClosedTasks || !canReopenTask(task.moduleName)
          "
          @click="handleChangeStatus('open')"
        >
          {{ $t('reopen') }} {{ $tc('task') }}
        </MButton>
        <FlotoDeleteBtn
          v-if="allowTaskDeleteAction && canArchiveTask(task.moduleName)"
          :message="$t('confirm_delete_item', { item: $tc('task') })"
          :show-tooltip="false"
          :disabled="!canArchiveTask(task.moduleName)"
          @confirm="$emit('remove')"
        >
          <MButton variant="error" outline>
            {{ $t('delete') }}
          </MButton>
        </FlotoDeleteBtn>
        <MButton variant="default" class="mx-1" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </slot>
    </template>
  </FlotoDrawer>
</template>

<script>
import Pick from 'lodash/pick'
import ConversationContainer from '@components/conversation/conversation-container'
import WorkLogContainer from '@components/worklog/worklog-container'
import AuditContainer from '@components/audit/audit-container'
import CheckListContainer from '@components/check-list/check-list-container'
import { getTaskResourceDetailApi } from '@modules/ticket/ticket-api'
import ResourceListTitleWithStatus from '@components/resource/resource-list-title-with-status.vue'
import Task from './task'
import { getTaskApi, getWorklogTotalTimeApi } from './api'
import { authComputed } from '@state/modules/auth'
import { CustomRulesComputed } from '@state/modules/custom-rules'
import AdditionalInfoWithExpand from '@components/additional-information/additional-info-expandable'
import { PreferenceComputed } from '@state/modules/preference'
import { StatusComputed } from '@state/modules/status'
import { FormComputed } from '@state/modules/form'
import { getFieldByRequesterAccess } from '@/src/modules/support-portal/helpers/field-access'
import { taskPermissionsMixin } from '@src/mixins/task-permissions'
export default {
  name: 'FlotoTaskDrawer',
  components: {
    Task,
    ConversationContainer,
    WorkLogContainer,
    AuditContainer,
    ResourceListTitleWithStatus,
    CheckListContainer,
    AdditionalInfoWithExpand,
  },
  mixins: [taskPermissionsMixin],
  props: {
    defaultTask: { type: Object, default: undefined },
    taskId: { type: Number, required: false, default: undefined },
    parentId: { type: Number, default: undefined },
    moduleName: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    detailsDisabled: { type: Boolean, default: false },
    hideConversation: { type: Boolean, default: false },
    hideWork: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    isResourceArchived: { type: Boolean, default: false },
  },
  data() {
    return {
      task: {
        ...(this.defaultTask || {}),
      },
      relatedResource: {},
      relatedResourceType: undefined,
      worklogTotalTime: undefined,
      remainingTime: undefined,
      estimatedTime: undefined,
      allowedWorklogAccess: '',
    }
  },
  computed: {
    ...authComputed,
    ...CustomRulesComputed,
    ...StatusComputed,
    ...authComputed,
    ...FormComputed,
    ...Pick(PreferenceComputed, [
      'allowUserToDeleteTasks',
      'allowUserToReopenClosedTasks',
    ]),
    allowTaskDeleteAction() {
      if (this.allowUserToDeleteTasks) {
        return (
          !this.disabled &&
          this.task.moduleName !== this.$constants.PROJECT &&
          !this.detailsDisabled
        )
      }
      return false
    },
    taskListeners() {
      const { close, ...listeners } = this.$listeners
      return listeners
    },
    resourceLink() {
      if (
        [
          this.$constants.REQUEST,
          this.$constants.PROBLEM,
          this.$constants.CHANGE,
        ].indexOf(this.moduleName || (this.defaultTask || {}).moduleName) >= 0
      ) {
        return this.$modules.getModuleRoute('ticket', 'view', {
          params: {
            ticketType: this.moduleName || (this.defaultTask || {}).moduleName,
            id: this.task.parentId,
          },
        })
      }
      return undefined
    },
    allowAuditContainer() {
      if (
        this.task.moduleName === this.$constants.CHANGE &&
        this.isPortalLogin
      ) {
        return false
      }
      return true
    },
    isDisabledUserToReopenClosedTasks() {
      const currentTask = this.taskStatus.find(
        (f) => f.key === this.task.statusId
      )
      if (this.disabled) {
        return true
      }
      if (this.detailsDisabled) {
        return true
      }
      if (
        currentTask &&
        !this.allowUserToReopenClosedTasks &&
        (currentTask.systemName || '').toLowerCase() === 'closed'
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    defaultTask(newValue) {
      if ((newValue || {}).id) {
        this.task = { ...newValue }
      }
    },
  },
  created() {
    this.getProjectCustomRules()
  },
  methods: {
    hasFieldViewPermission(field) {
      if (!this.isPortalLogin) {
        return true
      }
      const fields = this.taskPortalFields.filter((f) => {
        return f.paramName === field || f.key === field
      })
      if (fields.length === 0) {
        return false
      }
      return getFieldByRequesterAccess(fields, this.user).length > 0
    },
    handleChange(change) {
      this.$emit('update', { ...this.task, ...change })
    },
    handleChangeStatus(status) {
      const change = {
        statusId: this.taskFindStatus(status).id,
      }
      this.handleChange(change)
    },
    getTask() {
      if (this.task.id) {
        this.getRelatedResource()
        if (this.task.moduleName === this.$constants.PROJECT) {
          this.getWorklogTotalTime()
        }
        return
      }
      getTaskApi(this.moduleName, this.parentId, this.taskId).then((data) => {
        this.task = data
        this.getRelatedResource()
      })
    },
    getRelatedResource() {
      if (
        this.task.parentId &&
        this.task.moduleName !== this.$constants.SERVICE_CATALOG &&
        !this.isResourceArchived
      ) {
        getTaskResourceDetailApi(this.task.moduleName, this.task.parentId).then(
          (data) => {
            this.relatedResource = data
            this.relatedResourceType =
              this.task.moduleName || this.moduleName || this.$constants.REQUEST
          }
        )
      }
    },
    handleTabChange(tab) {
      if (tab === 'audit') {
        this.$refs &&
          this.$refs.auditContainer &&
          this.$refs.auditContainer.refresh()
      }
    },
    getWorklogTotalTime() {
      getWorklogTotalTimeApi(this.task.id).then((data) => {
        this.worklogTotalTime = data.worklogTotalTime
        this.estimatedTime = data.estimatedTime
        this.remainingTime = data.remainingTime
      })
    },
    getProjectCustomRules() {
      this.allowedWorklogAccess =
        this[`${this.$constants.PROJECT}WorklogAccess`] || ''
    },
  },
}
</script>
